import moment from "moment";
import { ALBUM_LAYOUTS, CAROUSEL_RATIOS, findAlbumLayout } from "../FdEditor/components/SubFdElements/weget/weget-image-module/albumLayouts";
import { useFonts } from "../FdDesignSetting/premiumDesign";
import { alignClass, spanStyle } from "./postContent";
import { focusPostModule, usePostPreviewDraft } from "./postPreview";

function Spans({ spans }) {
    return spans.map((span, i) => (span.bold
        ? <strong key={i} className="font-bold" style={spanStyle(span)}>{span.text}</strong>
        : <span key={i} style={spanStyle(span)}>{span.text}</span>));
}

function TextModule({ blocks }) {
    if (!blocks.some(b => b.spans.length)) {
        return <p className="text-[14px] text-gray-300">빈 텍스트</p>;
    }
    return blocks.map((block, i) => {
        if (!block.spans.length) {
            return <div key={i} className="h-[1.8em]" aria-hidden="true" />; // 빈 줄
        }
        if (block.type === 'HEADING') {
            return <h2 key={i} className={`mb-1 mt-6 ${alignClass(block.align)} whitespace-pre-wrap break-words text-[20px] font-bold leading-snug text-gray-900`}><Spans spans={block.spans} /></h2>;
        }
        return <p key={i} className={`${alignClass(block.align)} whitespace-pre-wrap break-words text-[15px] leading-[1.85] text-gray-800`}><Spans spans={block.spans} /></p>;
    });
}

function ImageModule({ module }) {
    const { layout, urls } = module;
    if (!urls.length) {
        return <div className="flex h-24 items-center justify-center rounded-lg bg-gray-50 text-[13px] text-gray-300">사진 없음</div>;
    }
    if (layout === 'SINGLE') {
        return urls.map((url, i) => <img key={i} src={url} alt="" className="w-full rounded-lg" />);
    }
    if (layout === 'CAROUSEL') {
        const aspect = CAROUSEL_RATIOS.find(r => r.value === module.ratio)?.aspect || 'aspect-square';
        return (
            <div className="flex snap-x snap-mandatory gap-1 overflow-x-auto rounded-lg">
                {urls.map((url, i) => <img key={i} src={url} alt="" className={`w-[85%] shrink-0 snap-center object-cover ${aspect}`} />)}
            </div>
        );
    }
    // 앨범: 장수가 배치에 안 맞으면(저장은 막힘) 2열 격자로
    const def = ALBUM_LAYOUTS[urls.length] ? findAlbumLayout(urls.length, module.albumLayout) : { container: 'grid grid-cols-2', cell: 'aspect-square' };
    return (
        <div className={`${def.container} gap-[2px] overflow-hidden rounded-lg`}>
            {urls.map((url, i) => (
                <div key={i} className={`relative bg-gray-100 ${def.cells?.[i] || def.cell}`}>
                    <img src={url} alt="" className="absolute inset-0 h-full w-full object-cover" />
                </div>
            ))}
        </div>
    );
}

/** 쓰는 중인 이야기 문서. onSelectModule(key): 모듈을 눌렀을 때 (PC 오른쪽 미리보기·모바일 미리보기 공용) */
export function PostPreviewArticle({ draft, onSelectModule }) {
    useFonts();
    if (!draft) {
        return null;
    }
    return (
        <article className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_28px_rgba(0,0,0,0.06)]">
            {draft.coverImageUrl && <img src={draft.coverImageUrl} alt="" className="aspect-[2/1] w-full object-cover" />}
            <div className="px-6 pb-8 pt-6">
                {draft.group?.trim() && <p className="mb-1.5 text-[12px] font-semibold text-gray-500">{draft.group.trim()}</p>}
                <h1 className={`break-words text-[24px] font-extrabold leading-tight ${draft.title.trim() ? 'text-gray-900' : 'text-gray-300'}`}>{draft.title.trim() || '제목'}</h1>
                <p className="mt-2 text-[12px] text-gray-400">
                    {moment(draft.publishedAt || Date.now()).format('YYYY.MM.DD HH:mm')}
                    {draft.visibility === 'PASSWORD' && ' · 🔒 비밀번호'}
                    {draft.visibility === 'PRIVATE' && ' · 나만 보기'}
                </p>
                <div className="mt-6 flex flex-col gap-1">
                    {draft.modules.map(module => (
                        // 안에 문단·제목이 들어가서 button 대신 role=button
                        <div
                            key={module.key}
                            role="button"
                            tabIndex={0}
                            onClick={() => onSelectModule(module.key)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelectModule(module.key))}
                            title="눌러서 이 부분 고치기"
                            className="-mx-2 my-1 cursor-pointer rounded-lg px-2 py-1 outline-none ring-[var(--mainColor)] transition hover:ring-2 focus-visible:ring-2"
                        >
                            {module.type === 'IMAGE' ? <ImageModule module={module} /> : <TextModule blocks={module.blocks} />}
                        </div>
                    ))}
                </div>
            </div>
        </article>
    );
}

/**
 * PC 오른쪽 이야기 미리보기 (이야기를 쓰는 동안 FdViewer 대신). 쓰는 중인 이야기를 문서처럼 보여 주고,
 * 모듈에 올리면 테두리, 누르면 그 모듈 편집기로 이동한다.
 */
export function FdPostPreview() {
    const draft = usePostPreviewDraft();

    return (
        <aside className="fixed bottom-0 right-0 top-14 flex w-[435px] flex-col border-l border-black/[0.06] bg-[#ececf0]" aria-label="이야기 미리보기">
            <div className="flex items-center justify-between px-5 py-3">
                <span className="text-xs font-bold tracking-wide text-gray-500">이야기 미리보기</span>
                <span className="text-[11px] text-gray-400">누르면 그 부분을 고쳐요</span>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-8">
                <PostPreviewArticle draft={draft} onSelectModule={focusPostModule} />
            </div>
        </aside>
    );
}
